import React from 'react';
import SidebarLayout from '../../dashboard/components/SidebarLayout';
import {AppTitle} from '../../../Presentation/components/ui/Typography';
import {useVisitsManagement} from '../hooks/useVisitsManagement';
import VisitsTable from '../components/VisitsTable';
import VisitsListFilters from '../components/filters/VisitsListFilters';
import VisitDetailsModal from '../components/VisitDetailsModal';
import VisitsAnalytics from '../components/VisitsAnalytics';
import {RefreshCw, List, BarChart3} from 'lucide-react';
import Tabs from '../../../Presentation/components/ui/Tabs';
import Card from '../../../Presentation/components/ui/Card';
// NotificationService removed - no longer needed for export functionality
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';
import {PermissionDeniedCard} from '../../../Presentation/components/common';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';

type ActiveTab = 'list' | 'analytics';

const VisitsPage: React.FC = () => {
  const {hasPermission} = useAuthorization();

  // Unified visits management
  const {
    // UI State
    isDetailsModalOpen, selectedVisit,
    statusFilter, handleStatusTabChange, applyFilters, clearFilters,
    actionError, actionErrorDetails, clearActionError,
    actionLoading,

    // Data
    visits, pagination,

    // Loading States
    loading,

    // Error States
    error, statisticsError,

    // Actions
    handleTabChange, handleVisitClick, handleCloseDetailsModal, handleRefresh,
    handlePageChange, handlePageSizeChange, handleApproveVisit, handleRejectVisit
  } = useVisitsManagement();

  // Export functionality removed - dashboard is for viewing only


  const statusTabs = [
    {key: 'all' as const, label: 'الكل'},
    {key: 'pending' as const, label: 'في الانتظار'},
    {key: 'approved' as const, label: 'موافق عليها'},
    {key: 'rejected' as const, label: 'مرفوضة'}
  ];

  const tabItems = [
    {
      key: 'list',
      label: 'قائمة الزيارات',
      description: 'عرض وإدارة جميع الزيارات',
      icon: <List className="w-5 h-5" />,
      badge: pagination?.total ?? 0,
      content: (
        <div className="space-y-6 p-6">
          {/* Status filter tabs */}
          <div className="flex gap-2 p-1 bg-gray-100 rounded-lg w-fit">
            {statusTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleStatusTabChange(tab.key)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  statusFilter === tab.key
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {/* Filter toolbar: brand / marketer-developer / store / date period */}
          <VisitsListFilters onApply={applyFilters} onClear={clearFilters} />
          <VisitsTable
            visits={visits}
            onVisitClick={handleVisitClick}
            page={pagination?.page || 1}
            pageSize={pagination?.limit || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE}
            totalCount={pagination?.total || 0}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            exportable={true}
            loading={loading}
          />
        </div>
      )
    },
    {
      key: 'analytics',
      label: 'التحليلات',
      description: 'إحصائيات واتجاهات الزيارات',
      icon: <BarChart3 className="w-5 h-5" />,
      content: (
        <div className="space-y-6 p-6">
          <VisitsAnalytics />
        </div>
      )
    }
  ];

  // Check if user has permission to view visits
  if (!hasPermission(PERMISSIONS.READ_VISITS) && !hasPermission(PERMISSIONS.MANAGE_VISITS)) {
    return (
      <SidebarLayout>
        <div className="space-y-6">
          <PermissionDeniedCard message="ليس لديك صلاحيات للوصول إلى إدارة الزيارات. يرجى التواصل مع المدير للحصول على الصلاحيات المناسبة." />
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <AppTitle>إدارة الزيارات</AppTitle>
          <div className="flex gap-3">
            {(hasPermission(PERMISSIONS.READ_VISITS) || hasPermission(PERMISSIONS.MANAGE_VISITS)) && (
              <button
                onClick={handleRefresh}
                className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                تحديث
              </button>
            )}
          </div>
        </div>

        {/* Error Display */}
        {(error || statisticsError) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{error || statisticsError}</p>
          </div>
        )}

        <Card className="p-0 overflow-hidden shadow-sm">
          <Tabs
            items={tabItems}
            defaultActiveKey="list"
            onChange={(key) => handleTabChange(key as ActiveTab)}
            variant="modern"
            className="flex-1"
          />
        </Card>

        {/* Tab content is now handled by the Tabs component */}

        {/* Visit Details Modal */}
        <VisitDetailsModal
          visit={selectedVisit}
          isOpen={isDetailsModalOpen}
          onClose={handleCloseDetailsModal}
          onApproveVisit={handleApproveVisit}
          onRejectVisit={handleRejectVisit}
          actionError={actionError}
          actionErrorDetails={actionErrorDetails}
          onClearError={clearActionError}
          actionLoading={actionLoading}
        />
      </div>
    </SidebarLayout>
  );
};

export default VisitsPage;
